import {createContext, useContext, useRef} from 'react'
import {createStore, useStore} from 'zustand'
import {devtools} from 'zustand/middleware'
import {createCodeownersSlice, type CodeownersSlice} from './slices/codeowners'
import {
  createComparisonInfoSlice,
  type ComparisonInfoSlice,
  type ComparisonInfoSliceOpts,
} from './slices/comparison-info'
import {createDiffFilterSlice, type DiffFilterSlice} from './slices/diff-filter'
import {createDiffSummariesSlice, type DiffSummariesSlice} from './slices/diff-summaries'
import {createMarkersSlice, type MarkersSlice, type MarkersSliceOpts} from './slices/markers'
import {
  createProgressiveDiffsSlice,
  type ProgressiveDiffsSlice,
  type ProgressiveDiffSliceOpts,
} from './slices/progressive-diffs'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import {getSelectedFullDiffHash, parsePathDigestWithoutLineNumbers} from '@github-ui/diff-lines/document-hash-helpers'
import {useWhitespacePreference} from '@github-ui/diff-view-settings/page-data/payloads/diff-view-settings'

type InitialState = ComparisonInfoSliceOpts & ProgressiveDiffSliceOpts & MarkersSliceOpts
type State = CodeownersSlice &
  ComparisonInfoSlice &
  DiffFilterSlice &
  DiffSummariesSlice &
  MarkersSlice &
  ProgressiveDiffsSlice
type PullRequestStore = ReturnType<typeof createPullRequestStore>

const PullRequestStoreContext = createContext<PullRequestStore | null>(null)

export function useDirectPullRequestStoreAccess() {
  const store = useContext(PullRequestStoreContext)
  if (!store) throw new Error('Missing PullRequestStoreProvider in the tree')
  return store
}

export function usePullRequestStore<T>(selector: (state: State) => T): T {
  const store = useDirectPullRequestStoreAccess()
  return useStore(store, selector)
}

export const PullRequestStoreProvider = ({children}: React.PropsWithChildren) => {
  const storeRef = useRef<PullRequestStore | null>(null)
  const prxFilesLiteThrottle = useFeatureFlag('prx_files_lite_throttle')
  const prxFilesMediumThrottle = useFeatureFlag('prx_files_medium_throttle')
  const routeData = useRoutePayload<FilesRoutePayload>()
  const hideWhitespace = useWhitespacePreference(routeData.user.viewSettings.hideWhitespace)

  if (!storeRef.current) {
    storeRef.current = createPullRequestStore({
      baseOid: routeData.pullRequest.comparison.baseOid,
      headOid: routeData.pullRequest.comparison.headOid,
      hideWhitespace,
      initialDiffSummaries: routeData.diffSummaries,
      initialLoadedPathDigests: new Set(routeData.diffContents.map(entry => entry.pathDigest)),
      markers: routeData.markers,
      pathName: routeData.pullRequest.pathName,
      prxFilesLiteThrottle,
      prxFilesMediumThrottle,
      selectedPathDigest: parsePathDigestWithoutLineNumbers(getSelectedFullDiffHash()),
    })
  }

  return <PullRequestStoreContext.Provider value={storeRef.current}>{children}</PullRequestStoreContext.Provider>
}

const createPullRequestStore = (initState: InitialState) => {
  return createStore<State>()(
    devtools(
      (...a) => ({
        ...createCodeownersSlice()(...a),
        ...createComparisonInfoSlice(initState)(...a),
        ...createDiffFilterSlice()(...a),
        ...createDiffSummariesSlice()(...a),
        ...createMarkersSlice(initState)(...a),
        ...createProgressiveDiffsSlice(initState)(...a),
      }),
      {name: 'pull-request-store'},
    ),
  )
}

try{ PullRequestStoreContext.displayName ||= 'PullRequestStoreContext' } catch {}
try{ PullRequestStoreProvider.displayName ||= 'PullRequestStoreProvider' } catch {}